import { useMemo } from "react";

import { useAuth } from "../context/Auth/AuthContext";
import {
	chooseSubscription,
	isSubscriptionActive,
	SUBSCRIPTION_TIERS,
	type Subscription,
	TIER_ORDER,
	type TierName,
	toTierName,
} from "../handlers/rewards";
import { usePayments } from "../services/create-resume/rewardQueries";

export { SUBSCRIPTION_TIERS, type TierName };

export interface SubscriptionStatus {
	/** True when the account holds at least one subscription whose `expireAt` is still in the future. */
	hasActiveSub: boolean;
	/**
	 * The effective access tier. When several subscriptions are active at once, the highest tier wins.
	 * With none active this is the free floor, `BRONZE`, so tier comparisons never have to guard null.
	 */
	tier: TierName;
	/**
	 * The subscription that decides `tier`: the highest active record, or — when nothing is active —
	 * the most recently lapsed one, so upsell copy can say "your SILVER expired on …". Null only when
	 * the account has never paid.
	 */
	subscription: Subscription | null;
	/** True while the payments query is still in flight — render a neutral state, not a locked one. */
	isLoading: boolean;
	/**
	 * True when the effective `tier` is at least `min`. A paid floor (e.g. `"SILVER"`) implies an
	 * active subscription, since only one raises `tier` above the free `BRONZE`.
	 */
	meetsTier: (min: TierName) => boolean;
}

function highestActive(payments: Subscription[]): Subscription | null {
	const active = payments.filter(isSubscriptionActive);
	if (active.length === 0) return null;

	return active.reduce((best, candidate) =>
		TIER_ORDER.indexOf(toTierName(candidate.package)) > TIER_ORDER.indexOf(toTierName(best.package)) ? candidate : best,
	);
}

/**
 * The account's live subscription standing, read from all of its payment records.
 *
 * `status: "ACTIVE"` on a record only describes its lifecycle, not whether it is valid today, so the
 * only trustworthy signal is `expireAt > now`. This hook applies that rule across every record and
 * reduces it to one answer the whole app can gate on. When two paid plans overlap, the higher tier
 * is the one that counts.
 */
export function useSubscription(): SubscriptionStatus {
	const { userData } = useAuth();
	const { data: payments = [], isLoading } = usePayments(userData?.userID);

	return useMemo(() => {
		const winner = highestActive(payments);
		const tier: TierName = winner ? toTierName(winner.package) : "BRONZE";
		return {
			hasActiveSub: winner !== null,
			tier,
			subscription: winner ?? chooseSubscription(payments),
			isLoading,
			meetsTier: (min: TierName) => TIER_ORDER.indexOf(tier) >= TIER_ORDER.indexOf(min),
		};
	}, [payments, isLoading]);
}
